import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import IVSBroadcastClient from 'amazon-ivs-web-broadcast';
import { useAuthHeaders } from '../hooks/useAuthHeaders';
import './admin/NCAdmin.css';

export default function BrowserStream({ config }) {
  const { gameId } = useParams();
  const { getAuthHeaders } = useAuthHeaders();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState(null);
  const [scoreboard, setScoreboard] = useState({
    visible: false,
    home: { name: '', points: 0, sets: 0 },
    away: { name: '', points: 0, sets: 0 },
  });

  const previewRef = useRef(null);     // IVS canvas (live)
  const previewVideoRef = useRef(null); // <video> element (pre-live preview)
  const previewStreamRef = useRef(null); // MediaStream for pre-live preview
  const clientRef = useRef(null);
  const wakeLockRef = useRef(null);
  const scoreCanvasRef = useRef(null);
  const scoreStreamRef = useRef(null);
  const liveStreamRef = useRef(null);
  const scoreboardRef = useRef(scoreboard); // ref so render loop always has latest state
  const renderLoopRef = useRef(null);
  const compositorVideoRef = useRef(null); // hidden <video> for compositor

  useEffect(() => {
    loadGame();
    enumerateDevices();
    return () => {
      cancelAnimationFrame(renderLoopRef.current);
      renderLoopRef.current = null;
      clientRef.current?.stopBroadcast();
      previewStreamRef.current?.getTracks().forEach(t => t.stop());
      wakeLockRef.current?.release();
    };
  }, []);

  // Start camera preview whenever selected camera changes (and not live)
  useEffect(() => {
    if (isLive || !selectedCamera) return;
    let active = true;
    (async () => {
      try {
        previewStreamRef.current?.getTracks().forEach(t => t.stop());
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCamera }, aspectRatio: 16/9, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        previewStreamRef.current = stream;
        if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;
      } catch (e) { console.error('Preview error:', e); }
    })();
    return () => { active = false; };
  }, [selectedCamera, isLive]);

  const loadGame = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games/${gameId}`);
      const data = await res.json();
      setGame(data.data);
      // Pre-populate scoreboard team names from game
      setScoreboard(prev => ({
        ...prev,
        home: { ...prev.home, name: data.data?.homeTeam || 'HOME' },
        away: { ...prev.away, name: data.data?.awayTeam || 'AWAY' },
      }));
    } catch (e) {
      setError('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const enumerateDevices = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      const audioDevices = devices.filter(d => d.kind === 'audioinput');
      setCameras(videoDevices);
      setMics(audioDevices);
      if (videoDevices[0]) setSelectedCamera(videoDevices[0].deviceId);
      if (audioDevices[0]) setSelectedMic(audioDevices[0].deviceId);
    } catch (e) {
      setError('Camera/microphone permission denied');
    }
  };

  // Keep ref in sync so render loop always has latest scoreboard state
  useEffect(() => { scoreboardRef.current = scoreboard; }, [scoreboard]);

  // Draw scoreboard onto a 2d context at the given dimensions
  const drawScoreboard = (ctx, W, H, sb) => {
    const barH = Math.round(H * 0.11);
    const y = H - barH;
    const mid = W / 2;

    if (!sb.visible) return;

    // Background + accent line
    ctx.fillStyle = 'rgba(0,0,0,0.78)';
    ctx.fillRect(0, y, W, barH);
    ctx.fillStyle = '#034a98';
    ctx.fillRect(0, y, W, Math.max(3, Math.round(barH * 0.05)));

    // Centre divider
    ctx.fillStyle = '#444';
    ctx.fillRect(mid - 2, y + barH * 0.15, 4, barH * 0.7);

    const ptSize   = Math.round(barH * 0.62);
    const nameSize = Math.round(barH * 0.50);
    const labelSize = Math.round(barH * 0.16);
    const setsNumSize = Math.round(barH * 0.28);

    const scoreOff = W * 0.08;
    const setsOff  = W * 0.05;
    const nameOff  = W * 0.18;

    // Vertical positions for stacked sets (label top, number bottom)
    const setsLabelY = y + barH * 0.32;
    const setsNumY   = y + barH * 0.72;

    // Scores (large, centred vertically)
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${ptSize}px Arial`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'right';
    ctx.fillText(String(sb.home.points).padStart(2, '0'), mid - scoreOff, y + barH / 2);
    ctx.textAlign = 'left';
    ctx.fillText(String(sb.away.points).padStart(2, '0'), mid + scoreOff, y + barH / 2);

    // Sets — stacked: "SETS" label above, number below
    ctx.textBaseline = 'middle';
    // Home sets (right-aligned, just inside score)
    ctx.font = `${labelSize}px Arial`;
    ctx.fillStyle = '#aaaaaa';
    ctx.textAlign = 'right';
    ctx.fillText('SETS', mid - setsOff, setsLabelY);
    ctx.font = `bold ${setsNumSize}px Arial`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(String(sb.home.sets), mid - setsOff, setsNumY);
    // Away sets (left-aligned, just inside score)
    ctx.font = `${labelSize}px Arial`;
    ctx.fillStyle = '#aaaaaa';
    ctx.textAlign = 'left';
    ctx.fillText('SETS', mid + setsOff, setsLabelY);
    ctx.font = `bold ${setsNumSize}px Arial`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(String(sb.away.sets), mid + setsOff, setsNumY);

    // Team names (outer, clipped)
    ctx.font = `bold ${nameSize}px Arial`;
    ctx.fillStyle = '#ffffff';
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, y, mid - nameOff, barH);
    ctx.clip();
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(sb.home.name.toUpperCase(), mid - nameOff, y + barH / 2);
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.rect(mid + nameOff, y, W - (mid + nameOff), barH);
    ctx.clip();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(sb.away.name.toUpperCase(), mid + nameOff, y + barH / 2);
    ctx.restore();
  };

  const toggleScoreboard = () => {
    setScoreboard(prev => ({ ...prev, visible: !prev.visible }));
  };

  const adjustScore = (team, field, delta) =>
    setScoreboard(prev => ({
      ...prev,
      [team]: { ...prev[team], [field]: Math.max(0, prev[team][field] + delta) },
    }));

  const startStream = async () => {
    setStarting(true);
    setError(null);
    try {
      // Use existing stream credentials if channel already created (via "Start Game" on My Streams)
      // Otherwise create the channel now (streamer went straight to browser stream page)
      let streamData;
      if (game.streamKey && game.ingestEndpoint) {
        streamData = { streamKey: game.streamKey, ingestEndpoint: game.ingestEndpoint };
      } else {
        const headers = await getAuthHeaders();
        const startRes = await fetch(`${config.apiUrl}/games/${gameId}/start`, { method: 'POST', headers });
        if (!startRes.ok) {
          const err = await startRes.json();
          throw new Error(err.message || 'Failed to start channel');
        }
        const res = await startRes.json();
        streamData = res.data;
      }

      // Stop preview, then reacquire with audio for broadcast
      previewStreamRef.current?.getTracks().forEach(t => t.stop());
      previewStreamRef.current = null;

      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedCamera }, aspectRatio: 16/9, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { deviceId: { exact: selectedMic } },
      });
      liveStreamRef.current = videoStream;

      const client = IVSBroadcastClient.create({
        streamConfig: IVSBroadcastClient.STANDARD_LANDSCAPE,
        ingestEndpoint: streamData.ingestEndpoint,
      });

      // Compositor canvas: draws camera video + scoreboard overlay at the camera's native resolution
      const videoTrack = videoStream.getVideoTracks()[0];
      const { width: rawW = 1280, height: rawH = 720 } = videoTrack.getSettings();
      // iOS Safari ignores aspectRatio constraint and may return portrait dimensions — force landscape
      const W = Math.max(rawW, rawH);
      const H = Math.min(rawW, rawH);
      const compCanvas = scoreCanvasRef.current;
      compCanvas.width = W;
      compCanvas.height = H;
      const compCtx = compCanvas.getContext('2d');

      // Hidden video element to draw camera frames from
      const camVideo = document.createElement('video');
      camVideo.srcObject = videoStream;
      camVideo.muted = true;
      compositorVideoRef.current = camVideo;
      await camVideo.play();

      // Render loop: camera frame + scoreboard overlay → compositor canvas
      const render = () => {
        if (!renderLoopRef.current) return;
        compCtx.drawImage(camVideo, 0, 0, W, H);
        drawScoreboard(compCtx, W, H, scoreboardRef.current);
        renderLoopRef.current = requestAnimationFrame(render);
      };
      renderLoopRef.current = requestAnimationFrame(render);

      // Feed compositor canvas stream to IVS instead of raw camera
      const compStream = compCanvas.captureStream(30);
      scoreStreamRef.current = compStream;
      await client.addVideoInputDevice(compStream, 'camera', { index: 0 });
      await client.addAudioInputDevice(videoStream, 'mic');
      client.attachPreview(previewRef.current);

      await client.startBroadcast(streamData.streamKey);

      clientRef.current = client;
      setIsLive(true);

      // Prevent screen from sleeping while streaming
      if ('wakeLock' in navigator) {
        try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch (e) { /* not critical */ }
      }
      await loadGame();
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const stopStream = async (end) => {
    setStopping(true);
    setShowStopModal(false);
    cancelAnimationFrame(renderLoopRef.current);
    renderLoopRef.current = null;
    compositorVideoRef.current?.pause();
    compositorVideoRef.current = null;
    clientRef.current?.stopBroadcast();
    clientRef.current = null;
    liveStreamRef.current = null;
    scoreStreamRef.current = null;
    wakeLockRef.current?.release();
    wakeLockRef.current = null;
    setIsLive(false);
    try {
      const headers = await getAuthHeaders();
      if (end) {
        navigate('/replays');
        fetch(`${config.apiUrl}/games/${gameId}/stop`, { method: 'POST', headers, body: JSON.stringify({ end: true }) });
        return;
      }
      await fetch(`${config.apiUrl}/games/${gameId}/stop`, {
        method: 'POST', headers, body: JSON.stringify({ end }),
      });
      await loadGame();
    } catch (e) {
      console.error(e);
    } finally {
      setStopping(false);
    }
  };

  if (loading) return (
    <div className="nc-admin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loading-spinner" />
    </div>
  );

  if (!game) return (
    <div className="nc-admin">
      <p>Game not found. <Link to="/my-streams">← Back</Link></p>
    </div>
  );

  const canStream = !!game.streamKey;

  const btnStyle = {
    background: 'var(--nc-surface)', border: '1px solid var(--nc-border)',
    borderRadius: 8, padding: '0.6rem 1rem', cursor: 'pointer',
    color: 'var(--nc-text)', fontSize: '1.3rem', fontWeight: 700,
    minWidth: 48, minHeight: 48, touchAction: 'manipulation',
  };

  const adjBtnStyle = (color) => ({
    ...btnStyle,
    background: color,
    border: 'none',
    color: '#fff',
    borderRadius: 10,
    fontSize: '1.5rem',
    minWidth: 52, minHeight: 52,
  });

  return (
    <div className="nc-admin">
      <Link to="/my-streams" style={{ fontSize: '0.875rem', color: 'var(--nc-accent)', marginBottom: '1.5rem', display: 'inline-block' }}>
        ← My Streams
      </Link>

      <div className="nc-admin-eyebrow">BROWSER STREAM</div>
      <h1 className="nc-admin-title" style={{ marginBottom: '0.5rem' }}>
        {game.homeTeam} vs {game.awayTeam}
      </h1>
      <p style={{ color: 'var(--nc-muted)', marginBottom: '1.5rem' }}>
        {game.sport} · {game.venue}{game.field ? ` — ${game.field}` : ''}
      </p>

      <div className="nc-bs-layout">
        {/* Left: preview */}
        <div className="nc-bs-preview">
          <div style={{ position: 'relative', background: '#000', borderRadius: 12, overflow: 'hidden' }}>
            {/* Pre-live camera preview */}
            <video
              ref={previewVideoRef}
              autoPlay muted playsInline
              style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: isLive ? 'none' : 'block' }}
            />
            {/* IVS live canvas */}
            <div style={{ width: '100%', aspectRatio: '16/9', display: isLive ? 'block' : 'none', overflow: 'hidden' }}>
              <canvas
                ref={previewRef}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            {!isLive && !selectedCamera && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '0.875rem' }}>
                No camera selected
              </div>
            )}
            {isLive && (
              <div style={{ position: 'absolute', top: 12, left: 12, background: '#ef4444', color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: 4, letterSpacing: '0.05em' }}>
                ● LIVE
              </div>
            )}
          </div>
        </div>

        {/* Right: controls */}
        <div className="nc-bs-controls">
          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {!isLive && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--nc-muted)', marginBottom: 4 }}>Camera</label>
                <select
                  value={selectedCamera}
                  onChange={e => setSelectedCamera(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', color: 'var(--nc-text)' }}
                >
                  {cameras.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 6)}`}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--nc-muted)', marginBottom: 4 }}>Microphone</label>
                <select
                  value={selectedMic}
                  onChange={e => setSelectedMic(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', color: 'var(--nc-text)' }}
                >
                  {mics.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0, 6)}`}</option>)}
                </select>
              </div>
            </div>
          )}

          {!isLive ? (
            <button
              className="nc-admin-btn primary"
              onClick={startStream}
              disabled={starting || !canStream || !selectedCamera}
              style={{ width: '100%' }}
            >
              {starting
                ? <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> Starting...</>
                : '● Go Live'}
            </button>
          ) : (
            <>
              <button
                className="nc-admin-btn"
                onClick={() => setShowStopModal(true)}
                disabled={stopping}
                style={{ background: '#ef4444', color: '#fff', width: '100%', marginBottom: '1.5rem' }}
              >
                {stopping
                  ? <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> Stopping...</>
                  : '■ Stop Stream'}
              </button>

              {/* Scoreboard controls — volleyball only */}
              {game.sport?.toLowerCase() === 'volleyball' && <div style={{ borderTop: '1px solid var(--nc-border)', paddingTop: '1.25rem' }}>
                <button
                  className="nc-admin-btn primary"
                  onClick={toggleScoreboard}
                  style={{ width: '100%', marginBottom: '1.25rem', background: scoreboard.visible ? '#034a98' : undefined }}
                >
                  {scoreboard.visible ? '🏐 Hide Scoreboard' : '🏐 Show Scoreboard'}
                </button>

                {/* Team name inputs + score controls — only shown when scoreboard is visible */}
                {scoreboard.visible && <>
                {/* Two-column layout: one column per team */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {[['home', 'Home'], ['away', 'Away']].map(([team, label]) => (
                    <div key={team} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <input
                        value={scoreboard[team].name}
                        onChange={e => setScoreboard(prev => ({ ...prev, [team]: { ...prev[team], name: e.target.value } }))}
                        style={{ padding: '0.5rem', borderRadius: 8, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', color: 'var(--nc-text)', fontSize: '0.85rem', textAlign: 'center', fontWeight: 600 }}
                      />
                      {[['points', 'pts'], ['sets', 'sets']].map(([field, flabel]) => (
                        <div key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <button onClick={() => adjustScore(team, field, -1)} style={adjBtnStyle('#c0392b')}>−</button>
                          <span style={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '1.3rem' }}>{scoreboard[team][field]}</span>
                          <button onClick={() => adjustScore(team, field, 1)} style={adjBtnStyle('#27ae60')}>+</button>
                          <span style={{ fontSize: '0.75rem', color: 'var(--nc-muted)', minWidth: 28 }}>{flabel}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setScoreboard(prev => ({
                    ...prev,
                    home: { ...prev.home, points: 0 },
                    away: { ...prev.away, points: 0 },
                  }))}
                  style={{ ...btnStyle, width: '100%', fontSize: '0.8rem', padding: '0.6rem', marginTop: '0.5rem' }}
                >
                  Reset Set Score
                </button>
                </>}
              </div>}
            </>
          )}
        </div>
      </div>

      {/* Hidden canvas for scoreboard overlay — must be in DOM for captureStream() */}
      <canvas ref={scoreCanvasRef} width={1280} height={720} style={{ display: 'none' }} />

      {showStopModal && (        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--nc-surface)', borderRadius: 12, padding: '2rem', maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem' }}>Stop streaming?</h3>
            <p style={{ color: 'var(--nc-muted)', fontSize: '0.875rem', margin: '0 0 1.5rem' }}>Choose how you'd like to stop.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button className="nc-admin-btn" onClick={() => stopStream(false)} style={{ textAlign: 'left' }}>
                <strong>Stop Streaming</strong>
                <span style={{ display: 'block', fontSize: '0.8rem', opacity: 0.7, fontWeight: 400 }}>Pause the stream — you can resume later</span>
              </button>
              <button className="nc-admin-btn" onClick={() => stopStream(true)} style={{ background: '#ef4444', color: '#fff', textAlign: 'left' }}>
                <strong>End Game</strong>
                <span style={{ display: 'block', fontSize: '0.8rem', opacity: 0.7, fontWeight: 400 }}>Finalise the game and process the recording</span>
              </button>
              <button className="nc-admin-btn" onClick={() => setShowStopModal(false)} style={{ background: 'transparent', border: '1px solid var(--nc-border)' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
